const forge = require('node-forge')

/**
 * Třída pro šifrování dat
 *
 * Copyright 2015 QCM, s.r.o. <http://www.qcm.cz>
 * @author Jiří Ťápal <jiri.tapal2(at)qcm.cz>
 *
 * @requires Forge <https://github.com/digitalbazaar/forge>
 * @requires Web Cryptography API <http://www.w3.org/TR/WebCryptoAPI/>
 * @requires File API <http://www.w3.org/TR/FileAPI/>
 *
 * @version 1.4.0
 */

/**
 * Vytvoří instanci CipherJS a provede inicializaci
 *
 * @param {string} publicKey veřejný klíč v PEM formátu
 *
 * @returns {CipherJS}
 */
export function CipherJS (publicKey) {
  this.init(publicKey)
};

(function () {

  /**
   * @type {Number}
   */
  var BYTES_IN_MEGABYTE = 1024 * 1024

  /**
   * Délka klíče, který je generován pro šifrování
   *
   * @type {Number}
   */
  var CIPHER_KEY_LENGTH = 16

  /**
   * Tento objekt je možné nastavit metodou CipherJS.setStringConstants() a přepsat tak veškeré textové konstanty
   *
   * @type {object}
   */
  var strings = {
    ERR_WRONG_INPUT_DATA: 'Neplatná vstupní data pro zašifrování.',
    ERR_CALLBACK_NOT_FUNCTION: 'Nastavená funkce pro zpracování chyb není funkce.',
    ERR_MAX_FILE_SIZE: 'Zvolený soubor překračuje maximální možnou velikost souboru.',
    FILE_EXTENSION_NOT_ALLOWED: 'Soubor má nepřípustný formát.',
    ERR_CIPHER_PROCESS: 'Chyba při šifrování.',
    ERR_EMPTY_PUBLIC_KEY: 'Veřejný klíč nemůže být prázdný.',
    ERR_LOAD_PUBLIC_KEY: 'Nepodařilo se načíst veřejný klíč. Špatný formát?',
    ERR_LOAD_FILE: 'Chyba při načítání souboru.',
    ERR_LIBRARIES_NOT_INCLUDED: 'Nejsou includovány potřebné knihovny, nebo prohlížeč nepodporuje potřebné API.',
    ERR_FILE_IS_NULL: 'Soubor k zašifrování je prázdný nebo není nastaven.'
  }

  /**
   * Callback funkce, která je volána v případě výskytu chyby.
   *
   * @type {function(err)}
   */
  var errCallback = null

  /**
   * Veřejný klíč k zašifrování symetrického klíče
   *
   * @type {forge.pki.publickKey}
   */
  var publicKey = null

  /**
   * Symetrický klíč pro šifrování dat
   *
   * @type {string}
   */
  var symetricKey = null

  /**
   * Inicializační vektor šifrovače
   *
   * @type {string}
   */
  var iv = null

  /**
   * Symetrický klíč zašifrovaný veřejným klíčem
   *
   * @type {string}
   */
  var encryptedSymetricKey = null

  CipherJS.prototype = {
    /**
     * Maximální velikost šifrovaného souboru v MB, default null => nekontroluje se
     *
     * @type {Number}
     */
    fileSizeLimit: null,
    /**
     * Povolené přípony pro soubory, default null => nekontroluje se
     *
     * @type {array}
     */
    allowedFileExtensions: null,
    /**
     * Zašifruje obsah souboru a zašifrovaná data vrátí jako parametr callback fce
     *
     * @param {File} file
     * @param {function(encryptedData)} callback
     *
     * @returns {ArrayBuffer} zašifrovaná data jako parametr callback funkce
     */
    encryptFile: function (file, callback) {
      this.checkFileSize(file.size)
      this.checkFileExtension(file.name)
      loadFile(file, function (fileContent) {
        encryptData(fileContent, function (encrypted) {
          callback(generateResponse(encrypted))
        })
      })
    },
    /**
     * Zašifruje textová data
     *
     * @param {string} strData
     * @param {function(encryptedData)} callback
     *
     * @returns {ArrayBuffer} zašifrovaná data jako parametr callback funkce
     */
    encryptData: function (strData, callback) {
      if (typeof strData == 'undefined') {
        return raiseError(new Error(strings.ERR_WRONG_INPUT_DATA))
      }
      encryptData(strData, function (encryptedData) {
        callback(generateResponse(encryptedData))
      })
    },
    /**
     * Nastaví maximální velikost pro šifrovaný soubor, která je následně při šifrování kontrolována.
     * Pokud není nastaveno (default), nebude se kontrolovat
     *
     * @param {Number} sizeInMB
     */
    setMaxFileSize: function (sizeInMB) {
      this.fileSizeLimit = sizeInMB
    },
    /**
     * Nastaví povolené přípony souborů pro šifrování.
     * Pokud není nastaveno (default), nebude se kontrolovat
     *
     * @param {array} allowedFileExtensions
     */
    setAllowedFileExtensions: function (allowedFileExtensions) {
      this.allowedFileExtensions = allowedFileExtensions
    },
    /**
     * Umožňuje nastavit objekt s textovými konstantami vlastními hodnotami, klíče/názvy parametrů viz strings
     *
     * @param {object} stringConstantsObj
     */
    setStringConstants: function (stringConstantsObj) {
      strings = stringConstantsObj
    },
    /**
     * Nastaví callback funkci, která je zavolána vždy v případě výskytu chyby.
     * Pokud není nastaveno, jsou vyhazovány výjimky.
     *
     * @param {function(err)} errCallbackFnc
     */
    setErrorCallback: function (errCallbackFnc) {
      if (typeof errCallbackFnc !== 'function') {
        return raiseError(new Error(strings.ERR_CALLBACK_NOT_FUNCTION))
      }
      errCallback = errCallbackFnc
    },
    /**
     * Provede inicializaci šifrovače
     *
     * @param {string} publicKey veřejný klíč v PEM formátu
     */
    init: function (publicKey) {
      checkLibraries()
      loadPublicKey(publicKey)
      generateCipherKeys()
    },
    /**
     * Ověří zda velikost nepřesahuje definovanou maximální velikost souboru
     *
     * @param {Number} fileSizeInBytes
     */
    checkFileSize: function (fileSizeInBytes) {
      if (this.fileSizeLimit !== null &&
        fileSizeInBytes != undefined &&
        fileSizeInBytes > (this.fileSizeLimit * BYTES_IN_MEGABYTE)) {
        return raiseError(new Error(strings.ERR_MAX_FILE_SIZE))
      }
    },
    /**
     * Ověří zda přípona souboru je povolena pro zpracování
     *
     * @param {string} filename
     */
    checkFileExtension: function (filename) {
      if (this.allowedFileExtensions !== null && this.allowedFileExtensions.length > 0 &&
        !this.allowedFileExtensions.includes(filename.split('.').pop().toLowerCase())) {
        return raiseError(new Error(strings.FILE_EXTENSION_NOT_ALLOWED))
      }
    }
  }

  /**
   * Importuje klíč pro šifrování do formátu pro Web Cryptography
   *
   * @param {string} symetricKey
   * @returns {Promise}
   */
  var importCryptoKey = function (symetricKey) {
    if (!isMsCrypto()) {
      return window.crypto.subtle.importKey(
        'raw',
        str2Uint8Array(symetricKey),
        getCryptoOptions(),
        false,
        ['encrypt']
      )
    }
    try {
      return window.msCrypto.subtle.importKey(
        'raw',
        str2Uint8Array(symetricKey),
        getCryptoOptions(),
        false,
        ['encrypt']
      )
    } catch (err) {
      raiseError(err)
    }
  }

  /**
   * @param {Uint8Array} iv
   * @returns {Object}
   */
  var getCryptoOptions = function (iv) {
    var options = { name: 'AES-CBC' }
    if (typeof iv != 'undefined') {
      options.iv = iv
    }
    return options
  }

  /**
   * Zašifruje textová data a výsledek vrátí jako parametr callback fce
   *
   * @param {string|ArrayBuffer} data
   * @param {function(encryptedData)} callback
   * @returns {string} zašifrovaná data jako parametr callback funkce
   */
  var encryptData = function (data, callback) {
    var keyResult = importCryptoKey(symetricKey)
    if (!isMsCrypto()) {
      var encryptPromise = keyResult.then(function (key) {
        console.log('Začátek šifrování [' + getStamp() + ']')

        return window.crypto.subtle.encrypt(
          getCryptoOptions(str2Uint8Array(iv)),
          key,
          formatDataToEncrypt(data) // ArrayBuffer
        )
      }).catch(function (err) {
        console.log('Chyba při šifrování [' + err.message + ']')
        return raiseError(new Error(strings.ERR_CIPHER_PROCESS))
      })

      encryptPromise.then(function (encrypted) {
        completeEncryption(encrypted, callback)
      })
    } else {
      keyResult.oncomplete = function (event) {
        msCryptoEncrypt(event.target.result, data, callback)
      }
    }
  }

  /**
   * @param {string} encryptedData
   * @param {function} callback
   */
  var completeEncryption = function (encryptedData, callback) {
    console.log('Šifrování dokončeno [' + getStamp() + ']')
    callback(encryptedData)
  }

  /**
   * @param {Key} key
   * @param {string} data
   * @param {function} callback
   */
  var msCryptoEncrypt = function (key, data, callback) {
    try {
      console.log('Začátek šifrování pomocí msCrypto [' + getStamp() + ']')
      var encryptResult = window.msCrypto.subtle.encrypt(
        getCryptoOptions(str2Uint8Array(iv)),
        key,
        formatDataToEncrypt(data) // ArrayBuffer
      )
      encryptResult.oncomplete = function (event) {
        completeEncryption(event.target.result, callback)
      }
    } catch (err) {
      console.log('Chyba při šifrování [' + err.message + ']')
      return raiseError(new Error(strings.ERR_CIPHER_PROCESS))
    }
  }

  /**
   * Převede data do požadovaného formátu pro šifrování
   *
   * @param {string|ArrayBuffer} data
   * @returns {Uint8Array}
   */
  var formatDataToEncrypt = function (data) {
    if (typeof data == 'string') {
      return str2Uint8Array(data)
    }
    return data
  }

  /**
   * Načte veřejný klíč
   *
   * @param {string} key
   */
  var loadPublicKey = function (key) {
    if (key.length == 0) {
      return raiseError(new Error(strings.ERR_EMPTY_PUBLIC_KEY))
    }
    try {
      publicKey = forge.pki.publicKeyFromPem(formatPublicKeyString(key))
    } catch (err) {
      console.log('Chyba při načítání certifikátu [' + err.message + '][' + getStamp() + ']')
      return raiseError(new Error(strings.ERR_LOAD_PUBLIC_KEY))
    }
  }

  /**
   * Převede veřejný klíč do požadovaného formátu
   *
   * @param {string} key
   * @returns {String}
   */
  var formatPublicKeyString = function (key) {
    if (key.indexOf('-----BEGIN') < 0) {
      return '-----BEGIN PUBLIC KEY-----\n' + key + '\n-----END PUBLIC KEY-----'
    }
    return key
  }

  /**
   * Načte soubor a vrátí jeho obsah jako parametr callback fce
   *
   * @param {File} file
   * @param {function(fileContent)} callback
   * @returns {string} obsah souboru jako parametr callback fce
   */
  var loadFile = function (file, callback) {
    if (typeof file !== 'object') {
      return raiseError(new Error(strings.ERR_FILE_IS_NULL))
    }
    var fileReader = new FileReader()
    fileReader.onload = function () {
      callback(fileReader.result)
    }
    fileReader.onerror = function (event) {
      console.log('Chyba při načítání souboru! Code ' + event.target.error.code + '[' + getStamp() + ']')
      return raiseError(new Error(strings.ERR_LOAD_FILE), true)
    }
    if (file != undefined && file != null) {
      console.log('Začátek načítání souboru [' + getStamp() + ']')
      fileReader.readAsArrayBuffer(file)
    }
  }

  /**
   * Zkontroluje zda jsou naincludovné potřebné knihovny
   *
   * @throws {ReferenceError}
   */
  var checkLibraries = function () {
    if ((typeof File == 'undefined') ||
      (typeof forge == 'undefined') ||
      (!isMsCrypto() && typeof window.crypto == 'undefined') ||
      (!isMsCrypto() && typeof window.crypto.subtle == 'undefined') ||
      (isMsCrypto() && typeof window.msCrypto.subtle == 'undefined')) {
      return raiseError(new ReferenceError(strings.ERR_LIBRARIES_NOT_INCLUDED), true)
    }
  }

  /**
   * Jedná se o IE a starší verzi Web Cryptography API?
   *
   * @returns {Boolean}
   */
  var isMsCrypto = function () {
    return (typeof window.msCrypto != 'undefined')
  }

  /**
   * Zpracuje chybu
   *
   * @param {Error} err
   * @param {boolean} rethrow
   */
  var raiseError = function (err, rethrow) {
    if (typeof rethrow == 'undefined') {
      rethrow = false
    }
    if (typeof errCallback == 'function') {
      errCallback(err)
      if (rethrow) {
        throw err
      }
    } else {
      throw err
    }
  }

  /**
   * Vygeneruje klíče potřebné k šifrování
   */
  var generateCipherKeys = function () {
    symetricKey = forge.random.getBytesSync(CIPHER_KEY_LENGTH)
    iv = forge.random.getBytesSync(CIPHER_KEY_LENGTH)
    encryptedSymetricKey = publicKey.encrypt(symetricKey)
  }

  /**
   * Sestaví odpověď se zašifrovanými daty skládající se ze segmentů A B C:
   * A - 32bit, obsahuje údaje o délce segmentu B v bytech;
   * B - zašifrovaný symetrický klíč délky jak je uvedeno v A;
   * C - inicializační vektor délky CIPHER_KEY_LENGTH + zašifrovaná data;
   *
   * @param {ArrayBuffer} encryptedData
   * @returns {ArrayBuffer}
   */
  var generateResponse = function (encryptedData) {
    var tmp = mergeBuffer(str2Uint8Array(getEncryptedSymetricKeyLength()), str2Uint8Array(encryptedSymetricKey))
    tmp = mergeBuffer(tmp, str2Uint8Array(iv))
    return mergeBuffer(tmp, new Uint8Array(encryptedData))
  }

  /**
   * Vrátí 32bit dlouhý řetězeč obsahující délku zašifrovaného symetrického klíče v bytech
   *
   * @returns {String}
   */
  var getEncryptedSymetricKeyLength = function () {
    return pack(encryptedSymetricKey.length)
  }

  /**
   * @param {string} str
   * @returns {Uint8Array}
   */
  var str2Uint8Array = function (str) {
    var uint = new Uint8Array(str.length)
    for (var i = 0; i < str.length; ++i) {
      uint[i] = str.charCodeAt(i)
    }
    return uint
  }

  /**
   * Vrátí now()
   *
   * @returns {String}
   */
  var getStamp = function () {
    var date = new Date()
    return date.toLocaleString()
  }

  /**
   * @param {ArrayBuffer} buffer1
   * @param {ArrayBuffer} buffer2
   * @returns {Uint8Array}
   */
  var mergeBuffer = function (buffer1, buffer2) {
    var tmp = new Uint8Array(buffer1.byteLength + buffer2.byteLength)
    tmp.set(new Uint8Array(buffer1), 0)
    tmp.set(new Uint8Array(buffer2), buffer1.byteLength)
    return tmp.buffer
  }

  /**
   * Zakóduje číslo do 32bit v binární soustavě
   *
   * @param {int} num
   * @returns {String}
   */
  var pack = function (num) {
    var result = ''
    result += String.fromCharCode(num >> 24 & 0xFF)
    result += String.fromCharCode(num >> 16 & 0xFF)
    result += String.fromCharCode(num >> 8 & 0xFF)
    result += String.fromCharCode(num & 0xFF)
    return result
  }

})()
